import React, { useMemo } from "react";
import { motion } from "framer-motion";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

// Pastel front + a darker "shade" used for the chunky extruded 3D shadow.
const PASTELS = [
  { bg: "#FFE7A6", shade: "#F4CD63" }, // yellow
  { bg: "#E7E0FF", shade: "#C5B8F2" }, // lavender
  { bg: "#CDF3DC", shade: "#A2E2BB" }, // mint
  { bg: "#FFD7E0", shade: "#F4AFC0" }, // pink
  { bg: "#FFE0CC", shade: "#F4C09E" }, // peach
  { bg: "#CFE6FF", shade: "#A3CBF4" }, // sky
];

const characterImage = (c) =>
  c.animated_image_url ||
  c.handdrawn_image_url ||
  c.image_url ||
  "/assets/handdrawn-character.png";

// ── A single cast card — pastel, extruded, with an overlapping name plate ──
const CharacterCard = ({ character, count, pal, onClick }) => (
  <motion.button
    type="button"
    onClick={() => onClick?.(character)}
    whileHover={{ y: -5 }}
    whileTap={{ scale: 0.98 }}
    transition={{ type: "spring", stiffness: 320, damping: 22 }}
    className="text-left"
    style={{
      position: "relative",
      width: "100%",
      background: pal.bg,
      borderRadius: 22,
      padding: 5,
      border: "none",
      cursor: "pointer",
      outline: "none",
      boxShadow: `0px 6px 0px ${pal.shade}, 0px 16px 30px rgba(11,26,51,0.14)`,
    }}
    aria-label={`Open ${character.name || "character"}`}
  >
    {/* Character portrait */}
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "1 / 1",
        borderRadius: 17,
        overflow: "hidden",
        background: pal.bg,
      }}
    >
      <img
        src={characterImage(character)}
        alt={character.name || "Character"}
        className="w-full h-full object-cover"
        style={{ objectPosition: "center top" }}
        loading="lazy"
        onError={(e) => { e.target.src = "/assets/handdrawn-character.png"; }}
      />
      {/* Glossy top sheen for a 3D feel */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0) 24%)",
          pointerEvents: "none",
        }}
      />
    </div>

    {/* Floating frosted-glass name plate (overlaps the portrait → layered depth) */}
    <div
      style={{
        position: "absolute",
        left: 9,
        right: 9,
        bottom: 9,
        background: "rgba(255,255,255,0.66)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.55)",
        borderRadius: 13,
        padding: "5px 10px",
        boxShadow: "0px 4px 12px rgba(11,26,51,0.16)",
      }}
    >
      <p
        style={{
          ...FREDOKA,
          color: "#0B1A33",
          fontWeight: 700,
          fontSize: 13.5,
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
          margin: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {character.name || "Unnamed"}
      </p>
      <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 11.5, fontWeight: 600, lineHeight: 1.1, margin: 0 }}>
        {count} {count === 1 ? "story" : "stories"}
      </p>
    </div>
  </motion.button>
);

// ── Empty state ──
const EmptyState = ({ onCreate }) => (
  <div
    className="p-10 text-center"
    style={{
      background: "#FFFFFF",
      border: "2px solid rgba(11,26,51,0.07)",
      borderRadius: 24,
      boxShadow: "0px 4px 0px rgba(11,26,51,0.05), 0px 12px 26px rgba(11,26,51,0.08)",
    }}
  >
    <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 17, margin: "0 0 4px" }}>
      No characters yet
    </p>
    <p style={{ ...FREDOKA, color: "#8A98AD", fontSize: 14, margin: "0 0 16px" }}>
      Create your first character to build your cast.
    </p>
    <button
      type="button"
      onClick={() => onCreate?.()}
      className="px-5 py-2.5 transition-all active:scale-[0.98]"
      style={{
        ...FREDOKA,
        background: "#0065F8",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 14,
        border: "none",
        borderRadius: 999,
        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.20)",
        cursor: "pointer",
      }}
    >
      + Create your first character
    </button>
  </div>
);

/**
 * CharacterStage — "Your Cast" cards.
 * - Collapsed: a horizontal scroll row with mandatory x-snap.
 * - Expanded: a responsive grid that wraps.
 * Per-character story counts are derived from the `stories` prop.
 */
const CharacterStage = ({
  characters = [],
  stories = [],
  expanded = false,
  onCharacterClick,
  onCreate,
}) => {
  // Count how many stories each character appears in.
  const counts = useMemo(() => {
    const map = {};
    (stories || []).forEach((s) => {
      const cs = Array.isArray(s.characters) ? s.characters : [];
      cs.forEach((c) => {
        const id = c && typeof c === "object" ? c.id : c;
        if (id != null) map[String(id)] = (map[String(id)] || 0) + 1;
      });
    });
    return map;
  }, [stories]);

  if (characters.length === 0) {
    return <EmptyState onCreate={onCreate} />;
  }

  const card = (character, i) => (
    <CharacterCard
      key={character.id}
      character={character}
      count={counts[String(character.id)] || 0}
      pal={PASTELS[i % PASTELS.length]}
      onClick={onCharacterClick}
    />
  );

  if (expanded) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(158px, 1fr))",
          gap: 18,
          paddingBottom: 10,
        }}
      >
        {characters.map((c, i) => card(c, i))}
      </div>
    );
  }

  return (
    <div
      className="cs-scroller"
      style={{
        display: "flex",
        gap: 18,
        overflowX: "auto",
        scrollSnapType: "x mandatory",
        paddingBottom: 12,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {characters.map((c, i) => (
        <div key={c.id} className="cs-card" style={{ scrollSnapAlign: "start" }}>
          {card(c, i)}
        </div>
      ))}
      <style>{`
        .cs-scroller::-webkit-scrollbar { display: none; }
        /* Exactly N whole cards fill the row width (no half-card peeking). */
        .cs-card { flex: 0 0 calc((100% - 32px) / 3); }
        @media (min-width: 640px) { .cs-card { flex-basis: calc((100% - 64px) / 5); } }
        @media (min-width: 1024px) { .cs-card { flex-basis: calc((100% - 96px) / 7); } }
      `}</style>
    </div>
  );
};

export default CharacterStage;
